import React, { useState, useEffect } from 'react';
import { Modal, Table, Tag, DatePicker, Select, Button, Space, Spin } from 'antd';
import { FilterOutlined, ReloadOutlined } from '@ant-design/icons';
import moment from 'moment';
import { GetPlanHistoryApi } from '../../api/plans/GetPlanHistoryApi';

const { RangePicker } = DatePicker;
const { Option } = Select;

const PlanHistoryModal = ({
  visible,
  onClose,
  planId,
  planTitle
}) => {
  const [loading, setLoading] = useState(false);
  const [historyData, setHistoryData] = useState([]);
  const [filters, setFilters] = useState({
    actionType: '',
    dateRange: null,
  });

  useEffect(() => {
    if (visible && planId) {
      fetchHistory();
    }
  }, [visible, planId]); // fetchHistory is defined inside the component and depends on filters state

  const fetchHistory = async () => {
    setLoading(true);
    try {
      // Start with empty filter params to get ALL records by default
      const filterParams = {};
      
      // Only add action type filter if user explicitly selected one
      if (filters.actionType && filters.actionType !== '' && filters.actionType !== 'ALL') {
        filterParams.actionType = filters.actionType;
      }
      
      // Only add date filters if user explicitly selected a date range
      if (filters.dateRange && Array.isArray(filters.dateRange) && filters.dateRange.length === 2) {
        filterParams.fromDate = filters.dateRange[0].format('YYYY-MM-DD');
        filterParams.toDate = filters.dateRange[1].format('YYYY-MM-DD');
      }

      const response = await GetPlanHistoryApi(planId, filterParams);
      
      if (response && response.success) {
        // Filter out records with no actual changes (except CREATED records)
        const filteredData = (response.data || []).filter(record => {
          // Always show CREATED records
          if (record.actionType === 'CREATED') {
            return true;
          }
          
          // For UPDATED records, only show if there are actual changes
          if (record.actionType === 'UPDATED') {
            return record.changedFields && 
                   typeof record.changedFields === 'object' && 
                   Object.keys(record.changedFields).length > 0;
          }
          
          // Show other action types by default
          return true;
        });
        setHistoryData(filteredData);
      } else {
        setHistoryData([]);
      }
    } catch (error) {
      setHistoryData([]);
    }
    setLoading(false);
  };

  const handleFilterChange = (key, value) => {
    setFilters(prev => ({ ...prev, [key]: value }));
  };

  const applyFilters = () => {
    fetchHistory();
  };

  const clearFilters = () => {
    setFilters({
      actionType: '',
      dateRange: null,
    });
    // Trigger re-fetch after state update
    setTimeout(() => fetchHistory(), 100);
  };

  const getActionTypeColor = (actionType) => {
    switch (actionType) {
      case 'CREATED':
        return 'green';
      case 'UPDATED':
        return 'blue';
      default:
        return 'default';
    }
  };

  const columns = [
    {
      title: 'Action',
      dataIndex: 'actionType',
      key: 'actionType',
      width: 100,
      render: (actionType) => (
        <Tag color={getActionTypeColor(actionType)}>
          {actionType}
        </Tag>
      ),
    },
    {
      title: 'Date',
      dataIndex: 'actionDate',
      key: 'actionDate',
      width: 150,
      render: (date) => moment(date).format('DD/MM/YYYY HH:mm'),
    },
    {
      title: 'By',
      dataIndex: 'actionByName',
      key: 'actionByName',
      width: 150,
      render: (name, record) => {
        // If actionByName exists, use it, otherwise try to get from actionByUser relation
        if (name) return name;
        if (record.actionByUser) {
          const firstName = record.actionByUser.firstName || '';
          const lastName = record.actionByUser.lastName || '';
          return `${firstName} ${lastName}`.trim() || record.actionBy;
        }
        return record.actionBy;
      },
    },
    {
      title: 'Changes Made',
      dataIndex: 'changedFields',
      key: 'changesMade',
      width: 400,
      render: (changedFields, record) => {
        if (record.actionType === 'CREATED') {
          return (
            <div style={{ padding: '8px', backgroundColor: '#f6ffed', border: '1px solid #b7eb8f', borderRadius: '4px' }}>
              <strong style={{ color: '#52c41a' }}>Plan Created</strong>
            </div>
          );
        }
        
        if (changedFields && typeof changedFields === 'object' && record.previousValues) {
          const changes = [];
          Object.entries(changedFields).forEach(([field, newValue]) => {
            const oldValue = record.previousValues[field];
            if (field !== 'targetDate' || newValue !== oldValue) {
              changes.push(
                <div key={field} style={{ 
                  display: 'flex', 
                  justifyContent: 'space-between', 
                  alignItems: 'center',
                  padding: '6px 8px',
                  margin: '2px 0',
                  backgroundColor: '#ffffff',
                  border: '1px solid #e5e7eb',
                  borderRadius: '4px'
                }}>
                  <div style={{ flex: 1 }}>
                    <div style={{ fontWeight: 'bold', fontSize: '12px', marginBottom: '4px', color: '#000000' }}>{field.charAt(0).toUpperCase() + field.slice(1)}</div>
                    <div style={{ fontSize: '12px', fontWeight: 'bold', color: '#374151', marginBottom: '2px' }}>Previous:</div>
                    <div style={{ fontSize: '11px', color: '#000000', padding: '2px 4px', backgroundColor: 'transparent', borderRadius: '2px' }}>
                      {field === 'finalized' ? (oldValue === 1 || oldValue === '1' || oldValue === true ? 'Yes' : 'No') : (oldValue || 'N/A')}
                    </div>
                  </div>
                  <div style={{ 
                    width: '2px', 
                    height: '50px', 
                    backgroundColor: '#d9d9d9', 
                    margin: '0 12px'
                  }}></div>
                  <div style={{ flex: 1, textAlign: 'left' }}>
                    <div style={{ fontSize: '12px', fontWeight: 'bold', color: '#374151', marginBottom: '2px', marginTop: '20px' }}>New:</div>
                    <div style={{ fontSize: '11px', color: '#52c41a', padding: '2px 4px', backgroundColor: '#f6ffed', borderRadius: '2px' }}>
                      {field === 'finalized' ? (newValue === 1 || newValue === '1' || newValue === true ? 'Yes' : 'No') : (newValue || 'N/A')}
                    </div>
                  </div>
                </div>
              );
            }
          });
          return changes.length > 0 ? <div>{changes}</div> : '-';
        }
        
        return '-';
      },
    },
  ];

  const handleModalClose = () => {
    onClose();
  };

  return (
    <Modal
      title={`Plan History - ${planTitle}`}
      open={visible}
      onCancel={handleModalClose}
      footer={null}
      width={1200}
      className="plan-history-modal"
      maskClosable={true}
      getContainer={() => document.body}
      destroyOnClose={true}
      style={{ top: 20 }}
      zIndex={10001}
      mask={true}
      maskStyle={{ zIndex: 10000 }}
      closeIcon={true}
    >
      <div>

      <Spin spinning={loading}>
        <div 
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            e.stopImmediatePropagation();
            return false;
          }} 
          onMouseDown={(e) => {
            e.preventDefault();
            e.stopPropagation();
            e.stopImmediatePropagation();
            return false;
          }}
          onPointerDown={(e) => {
            e.preventDefault();
            e.stopPropagation();
            e.stopImmediatePropagation();
            return false;
          }}
          style={{ position: 'relative', zIndex: 10001 }}
        >
          <Table
            columns={columns}
            dataSource={historyData}
            rowKey="id"
            pagination={{
              pageSize: 10,
              showSizeChanger: true,
              showTotal: (total, range) =>
                `${range[0]}-${range[1]} of ${total} items`,
            }}
            scroll={{ y: 400 }}
            onRow={() => ({
              onClick: (e) => {
                e.preventDefault();
                e.stopPropagation();
                e.stopImmediatePropagation();
                return false;
              },
              onMouseDown: (e) => {
                e.preventDefault();
                e.stopPropagation();
                e.stopImmediatePropagation();
                return false;
              },
              onMouseUp: (e) => {
                e.preventDefault();
                e.stopPropagation();
                e.stopImmediatePropagation();
                return false;
              },
            })}
            components={{
              body: {
                wrapper: (props) => (
                  <tbody 
                    {...props} 
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      e.stopImmediatePropagation();
                      return false;
                    }} 
                  />
                ),
                row: (props) => (
                  <tr 
                    {...props} 
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      e.stopImmediatePropagation();
                      return false;
                    }} 
                  />
                ),
                cell: (props) => (
                  <td 
                    {...props} 
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      e.stopImmediatePropagation();
                      return false;
                    }} 
                  />
                ),
              },
            }}
          />
        </div>
      </Spin>
      </div>
    </Modal>
  );
};

export default PlanHistoryModal;
